import { useEffect, useMemo, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { PageHeader, PageShell, Stat } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { WifiOff, RefreshCw } from 'lucide-react';
import BreakWindowSettings from '@/components/BreakWindowSettings';

function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return iso; }
}

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export default function OfflineLog() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [from, setFrom] = useState(isoDaysAgo(7));
  const [to, setTo] = useState(isoToday());
  const [userId, setUserId] = useState('all');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from_date: from, to_date: to };
      if (userId !== 'all') params.user_id = userId;
      const { data } = await api.get('/offline-logs', { params });
      setRows(data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, userId]);
  useEffect(() => { api.get('/users').then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const totals = useMemo(() => {
    const open = rows.filter(r => !r.came_online_at).length;
    const closed = rows.filter(r => r.came_online_at).length;
    const totalMin = rows.reduce((s, r) => s + (r.duration_minutes || 0), 0);
    return { open, closed, totalMin };
  }, [rows]);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Admin only"
        title="Offline Log"
        subtitle="Tracking runs only between each user's punch-in and punch-out. Every 10-minute silent stretch during work hours lands here."
        actions={<Button variant="outline" onClick={load} data-testid="offlinelog-refresh"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>}
      />

      <BreakWindowSettings />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Stat testid="offlinelog-open" label="Currently offline" value={totals.open} hint=">10m no heartbeat" accent />
        <Stat testid="offlinelog-closed" label="Recovered events" value={totals.closed} hint="Came back online" />
        <Stat testid="offlinelog-mins" label="Total offline minutes" value={totals.totalMin} hint="Across window" />
      </div>

      <div className="tactical-card p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="offlinelog-from" className="w-40" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="offlinelog-to" className="w-40" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">User</Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-56" data-testid="offlinelog-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto text-xs text-muted-foreground">{loading ? 'Loading…' : `${rows.length} event(s)`}</div>
      </div>

      <div className="tactical-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Went offline</TableHead>
              <TableHead>Came online</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="offlinelog-tbody">
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8"><WifiOff className="w-4 h-4 inline mr-2" />No offline events in this window. Team's been steady.</TableCell></TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`offlinelog-row-${r.id}`}>
                <TableCell className="font-medium">{r.user_name}</TableCell>
                <TableCell><Badge variant="outline" className="uppercase text-[10px] tracking-widest">{r.role || '—'}</Badge></TableCell>
                <TableCell className="text-xs">{fmt(r.went_offline_at)}</TableCell>
                <TableCell className="text-xs">{fmt(r.came_online_at)}</TableCell>
                <TableCell className="font-mono">{r.duration_minutes != null ? `${r.duration_minutes}m` : <span className="text-red-500 font-bold">ongoing</span>}</TableCell>
                <TableCell>{r.came_online_at
                  ? <Badge variant="outline" className="text-emerald-600 border-emerald-500/30 bg-emerald-500/10">recovered</Badge>
                  : <Badge variant="outline" className="text-red-600 border-red-500/30 bg-red-500/10">offline</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </PageShell>
  );
}
